/* eslint-disable @typescript-eslint/no-explicit-any */
import { baseSecondGuaranteApi } from "./baseSecondGuaranteeApis";
import {
  CurpResponse,
  DocumentResponse,
  getPepsResponse,
  getPersonResponse,
  PatchAddressResponse,
  PepsStructure,
  PersonaStructure,
  PostValidateNextStageResponse,
  SignedUrlFileResponse,
} from "@interfaces/response";
import {
  CurpQuery,
  PayloadAddressPatch,
  PayloadDocument,
  PayloadDocumentGenerate,
  PayloadPersonPatch,
  PayloadRequestPhoneCode,
  PayloadSignUrl,
  PayloadUploadFile,
  PayloadValidatePhoneCode,
} from "@interfaces/request";
import { BuroValidatorRequestPF, BuroValidatorRequestPFAE } from "@api/sat_apis_new/satApsi";
import {
  BankInformationPayload,
  BankInformationResponse,
  RequestStatusConfigSecondGuarantee,
} from "@interfaces/SecondGuarantee";
import { UUID } from "crypto";
import { LoanApplication } from "@interfaces/store";
import { LoanStatus } from "@interfaces/catalogs";
import { setLoanStatus } from "@store/slices/registerSlice";

const secondGuaranteeApi = baseSecondGuaranteApi.injectEndpoints({
  endpoints: (build) => ({
    getPersonByIdGuarantee: build.query<PersonaStructure, string>({
      query: (personId) => `personas/${personId}`,
      transformResponse: (response: any) => {
        return response.data;
      },
    }),
    getPersonsSecondGuarantee: build.query<
      PersonaStructure[],
      { loanApplicationId: string; personTypeId?: string; personTypeCode?: string[]; id?: string }
    >({
      query: ({ loanApplicationId, personTypeId, personTypeCode, id }) => {
        let url = `personas?q=loan_application_id:${loanApplicationId}`;
        if (id) url += `&q=id:${id}`;
        if (personTypeId) url += `&q=person_type_id:${personTypeId}`;
        if (personTypeCode?.length) {
          personTypeCode.forEach((item) => {
            url += `&q_person_type_code=${item}`;
          });
        }
        return {
          url,
        };
      },
      transformResponse: (response: getPersonResponse) => {
        return response.data.data;
      },
    }),
    patchPersonSecondGuarantee: build.mutation<Response, { personId: string; body: PayloadPersonPatch }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}`,
        method: "PATCH",
        body: body,
      }),
    }),
    patchUsuarioStatusSecondGuarantee: build.mutation<
      Response,
      { usuario: string; body: RequestStatusConfigSecondGuarantee }
    >({
      query: ({ usuario, body }) => ({
        url: `usuarios_avales/${usuario}`,
        method: "PATCH",
        body: body,
      }),
    }),
    getCurpSecondGuarantee: build.query<CurpResponse, CurpQuery>({
      query: (data) => ({
        url: "curp",
        params: data,
      }),
    }),
    getByCpSecondGuarantee: build.query<any, string>({
      query: (codigoPostal) => ({
        url: "codigo_postal",
        params: { postal_code: codigoPostal },
      }),
      transformResponse: (response: { data: any }) => {
        return response.data;
      },
    }),
    postAddressSecondGuarantee: build.mutation<PatchAddressResponse, PayloadAddressPatch>({
      query: (data) => ({
        url: `direcciones`,
        method: "POST",
        body: data,
      }),
    }),
    patchAddressSecondGuarantee: build.mutation<PatchAddressResponse, { addressId: string; data: PayloadAddressPatch }>(
      {
        query: ({ addressId, data }) => ({
          url: `direcciones/${addressId}`,
          method: "PATCH",
          body: data,
        }),
      },
    ),
    requestPhoneCodeSecondGuarantee: build.mutation<Response, any>({
      query: (data: PayloadRequestPhoneCode) => ({
        url: `telefono/enviar_codigo`,
        method: "POST",
        body: data,
      }),
    }),
    validatePhoneCodeSecondGuarantee: build.mutation<Response, any>({
      query: (data: PayloadValidatePhoneCode) => ({
        url: `telefono/validar_codigo`,
        method: "POST",
        body: data,
      }),
    }),
    postPersonPepsSecondGuarantee: build.mutation<Response, { personId: string; body: { peps: PepsStructure[] } }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}/peps`,
        method: "POST",
        body,
      }),
    }),
    getPersonPepsSecondGuarantee: build.query<PepsStructure[], { personId: string }>({
      query: ({ personId }) => `personas/${personId}/peps`,
      transformResponse: (response: getPepsResponse) => {
        return response?.data || [];
      },
    }),
    buroValidatorSecondGuarantee: build.mutation<Response, BuroValidatorRequestPF | BuroValidatorRequestPFAE>({
      query: ({ person_Type, data }) => ({
        url: `sat_core/credit_bureau_authorization/${person_Type}`,
        method: "POST",
        body: data,
      }),
    }),
    getBankAccountsSecondGuarantee: build.query<BankInformationResponse[], string>({
      query: (solicitudId: UUID) => ({
        url: `solicitudes/${solicitudId}/cuentas_bancarias`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        return response.data;
      },
    }),
    getDocumentAppIdSecondGuarantee: build.query<
      DocumentResponse[],
      { loanApplicationId: string; clientDetailsId?: string; personId?: string; status?: boolean }
    >({
      query: ({ loanApplicationId, clientDetailsId, personId, status }) => {
        let url = `documentos?loan_application_id=${loanApplicationId}`;

        if (clientDetailsId) url += `&client_details_id=${clientDetailsId}`;
        if (personId) url += `&person_id=${personId}`;
        if (status !== undefined) url += `&status=${status}`;

        return {
          url,
        };
      },
      transformResponse: (response: { data: DocumentResponse[] }) => {
        return response.data;
      },
    }),
    patchBankAccountSecondGuarantee: build.mutation<
      Response,
      { solicitudId: UUID | string; bankAccountId: UUID | string; body: BankInformationPayload }
    >({
      query: (data: { solicitudId: UUID | string; bankAccountId: UUID | string; body: BankInformationPayload }) => ({
        url: `solicitudes/${data.solicitudId}/cuentas_bancarias/${data.bankAccountId}`,
        method: "PATCH",
        body: data.body,
      }),
    }),
    postBankAccountsSecondGuarantee: build.mutation<
      Response,
      { solicitudId: UUID | string; body: BankInformationPayload }
    >({
      query: (data: { solicitudId: UUID; body: BankInformationPayload }) => ({
        url: `solicitudes/${data.solicitudId}/cuentas_bancarias`,
        method: "POST",
        body: data.body,
      }),
    }),
    uploadFileSecondGuarantee: build.mutation<Response, PayloadUploadFile>({
      query: (data: PayloadUploadFile) => ({
        url: data.url,
        method: "POST",
        body: data.body,
        formData: true,
      }),
    }),
    postDocumentSecondGuarantee: build.mutation<DocumentResponse, PayloadDocument>({
      query: (data: PayloadDocument) => ({
        url: "documentos",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { data: DocumentResponse }) => {
        return response.data;
      },
    }),
    patchDocumentSecondGuarantee: build.mutation<Response, { documentId: UUID | undefined; body: any }>({
      query: ({ documentId, body }) => ({
        url: `documentos/${documentId}`,
        method: "PATCH",
        body: body,
      }),
    }),
    getSignUrlToUploadSecondGuarantee: build.query<SignedUrlFileResponse, PayloadSignUrl>({
      query: (data: PayloadSignUrl) => ({
        url: `generar_url/subir`,
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { data: SignedUrlFileResponse }) => {
        return response.data;
      },
    }),
    postDocumentGenarateSecondGuarantee: build.mutation<Response, PayloadDocumentGenerate>({
      query: (data: PayloadDocumentGenerate) => ({
        url: "generar_documento",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { data: Response }) => {
        return response.data;
      },
    }),
    getLoanApplicationByIdSecondGuarantee: build.query<LoanApplication, string>({
      query: (soldId) => `solicitudes?application_id=${soldId}`,
      transformResponse: (response: { data: LoanApplication[] }) => {
        if (response.data.length === 0) {
          throw { status: 404, data: { message: "No se encontró la solicitud" } };
        }
        return response.data[0];
      },
    }),

    postValidateNextStageSecondGuarantee: build.mutation<
      PostValidateNextStageResponse,
      { applicationId: string; statusCode: LoanStatus | string; username: string; disable_sequence_check?: boolean }
    >({
      query: ({ applicationId, statusCode, username, disable_sequence_check }) => ({
        url: `solicitudes/${applicationId}/progreso`,
        method: "POST",
        body: {
          status_code: statusCode,
          user_name: username,
          disable_sequence_check: disable_sequence_check || undefined,
        },
      }),
      async onQueryStarted(arg, api) {
        const { statusCode } = arg;
        api.dispatch(setLoanStatus(statusCode as LoanStatus));
      },
    }),
  }),
});

export const {
  useLazyGetPersonByIdGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  useLazyGetCurpSecondGuaranteeQuery,
  useLazyGetByCpSecondGuaranteeQuery,
  usePostAddressSecondGuaranteeMutation,
  usePatchAddressSecondGuaranteeMutation,
  useRequestPhoneCodeSecondGuaranteeMutation,
  useValidatePhoneCodeSecondGuaranteeMutation,
  useLazyGetPersonsSecondGuaranteeQuery,
  usePostPersonPepsSecondGuaranteeMutation,
  useLazyGetPersonPepsSecondGuaranteeQuery,
  useBuroValidatorSecondGuaranteeMutation,
  usePatchUsuarioStatusSecondGuaranteeMutation,
  useLazyGetBankAccountsSecondGuaranteeQuery,
  useLazyGetDocumentAppIdSecondGuaranteeQuery,
  usePatchBankAccountSecondGuaranteeMutation,
  usePostBankAccountsSecondGuaranteeMutation,
  useUploadFileSecondGuaranteeMutation,
  usePostDocumentSecondGuaranteeMutation,
  usePatchDocumentSecondGuaranteeMutation,
  useLazyGetSignUrlToUploadSecondGuaranteeQuery,
  usePostDocumentGenarateSecondGuaranteeMutation,
  useLazyGetLoanApplicationByIdSecondGuaranteeQuery,
  usePostValidateNextStageSecondGuaranteeMutation,
} = secondGuaranteeApi;
